import {
    AfterViewInit,
    Component,
    ElementRef, Input,
    inject,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import { Hotspot } from "@pcp/core/interfaces";
import { PCP_LOGGER } from "@pcp/logging";
import { VimeoEmbedParameters, VideoQualityId } from "@vimeo/player";
import { from, ReplaySubject, Subject } from "rxjs";
import { filter, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { ReactivePlayer } from "./reactive-player";
import { VideoDebugLogger } from "./video-debug-logger";
import { VideoPlayerInputProps } from "./video-player-input";
import { VideoPlayerOptions } from "./video-player-options";
import { VideoPlayerConfigService } from "../../services/video-player-config.service";
import { ResizedDirective } from 'shared/ui/resize-observer';
import { VideoHotspotsComponent } from "../video-hotspots/video-hotspots.component";
import { AsyncPipe } from "@angular/common";


/**
 * A video could not start with sound and was retried muted.
 *
 * The scoped logger prefixes these with its domain, so the names in Application
 * Insights are `video.AutoplayBlocked` and `video.PlaybackError`.
 */
export const VIDEO_AUTOPLAY_BLOCKED_EVENT = 'AutoplayBlocked';
/** The video player reported a playback error. */
export const VIDEO_PLAYBACK_ERROR_EVENT = 'PlaybackError';

/**
 * TODO
 * E    automatischer Hotspot
 *      Bei erreichen von FrameStart wird automatisch auf das Element in ActionLeft gesprungen
 */

@Component({
    selector: 'pcp-video-player',
    templateUrl: './video-player.component.html',
    styleUrls: ['./video-player.component.scss'],
    imports: [ResizedDirective, VideoHotspotsComponent, AsyncPipe]
})
export class VideoPlayerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

    private readonly round = Math.floor;

    private readonly onDestroy$ = new Subject<void>();

    private readonly videoInputSubject = new ReplaySubject<VideoPlayerInputProps>(1);
    private readonly mutedInputSubject = new ReplaySubject<boolean>(1);

    /**
     * VimeoPlayer ID in DOM.
     * Can be changed to create multiple, unqiue video player instances.
     */
    @Input() vimeoPlayerDomId = 'player';

    @Input() video!: VideoPlayerInputProps;

    @Input() responsive = true;

    /**
     * Automatically start playing the video.
     * The player may pause the video immediately again after it has started playing.
     * This causes the player to start buffering in the paused state.
     */
    @Input() autoplay = true;

    /**
     * Start autoplaying the video in any case.
     */
    @Input() forceAutoplay = false;

    @Input() muted = false;
    @Input() quality: VideoQualityId | "auto" = "auto";
    readonly debug = VideoDebugLogger.enabled;

    /**
     * If true, the player won't be able to turn on any audio.
     * All other volume-related settings (muted, volume) will not apply.
     *
     * Valid only at the time of creating the player or loading a video.
     * If the video is showing video controls, the user will still be able to change the volume using the video controls.
     */
    @Input() forceMuted: boolean = false;

    /**
     * show or hide the player's video controls
     */
    @Input() videoControls: boolean = true;

    private player$ = new Subject<ReactivePlayer>();

    @Output() videoStartedPlaying = this.player$.pipe(switchMap(player => player.videoStartedPlaying$));
    @Output() videoPaused = this.player$.pipe(switchMap(player => player.videoPaused$)).pipe(
        // do not emit if the video was paused because:
        // - a pause point was reached
        // - it's the video range's start
        // - it's the video range's end
        // - it's the video's end
        tap(event => event.player.getSeeking().then(seeking => this.logger.log('emit paused event while seeking', seeking, event))),
        switchMap(event => from(event.player.getCurrentTime()).pipe(
            map(currentTime => this.round(currentTime)),
            filter(currentTime => !this.video.pausePoints?.includes(currentTime)),
            filter(currentTime => this.video.range?.start !== currentTime),
            filter(currentTime => this.video.range?.end !== currentTime),
            filter(() => event.percent < 1), // Do not emit video end
            map(() => event),
        )),
    );
    @Output() videoLoaded = this.player$.pipe(switchMap(player => player.videoLoaded$));
    @Output() seekedToPosition = this.player$.pipe(switchMap(player => player.seekedToPosition$));
    @Output() timeUpdate = this.player$.pipe(switchMap(player => player.timeUpdate$));
    @Output() hotspotClicked = this.player$.pipe(switchMap(player => player.hotspotClicked$));
    @Output() videoEnded = this.player$.pipe(switchMap(player => player.videoEnded$));

    readonly visibleHotspots$ = this.player$.pipe(switchMap(player => player.visibleHotspots$));

    @ViewChild('videoHotspots', { static: true }) videoHotspots!: ElementRef<HTMLDivElement>;

    private player!: ReactivePlayer;
    private readonly defaultPlayerOptions: VimeoEmbedParameters = {
        autopause: false,
        background: false,
        controls: true,
        muted: true,
        width: 640,
        autoplay: true,
        loop: false,
        quality: 'auto' as VideoQualityId,
        // Keep adaptive streaming, but never switch above 1080p:
        // SMARTBOARD browsers crash internally (~3s in) when the player switches up to 4k.
        // Videos are authored in 1080p anyway; Vimeo's upscaled 2k/4k renditions are never needed.
        max_quality: '1080p' as VideoQualityId,
        dnt: true,
        playsinline: true,
    };

    private readonly logger: VideoDebugLogger;

    /**
     * Unconditional telemetry, unlike `VideoDebugLogger`, which is console-only and
     * off by default. A video starting silently is reported by end users, so it has
     * to be observable without preparing the device first.
     */
    private readonly insightsLogger = inject(PCP_LOGGER).createScoped('video');

    constructor(
        private readonly zone: NgZone,
        private readonly hostElement: ElementRef,
        private readonly globalPlayerConfig: VideoPlayerConfigService,
    ) {
        this.logger = new VideoDebugLogger(this);
    }

    ngOnInit(): void {
        // TODO: commented out as unmuting can cause PrivacyErrors if the user has not interacted with the player yet
        // this.player$.pipe(
        //     switchMap(player => this.mutedInputSubject.pipe(
        //         switchMap(isMuted => player.setMuted(isMuted)),
        //     )),
        //     takeUntil(this.onDestroy$),
        // ).subscribe();
        this.player$.pipe(
            switchMap(player => this.videoInputSubject.pipe(
                switchMap(video => player.playVideo(video, this.createPlayerOptions(video))),
            )),
            takeUntil(this.onDestroy$),
        ).subscribe();

        this.player$.pipe(
            switchMap(player => player.autoplayBlocked$),
            takeUntil(this.onDestroy$),
        ).subscribe(event => this.insightsLogger.trackEvent(
            VIDEO_AUTOPLAY_BLOCKED_EVENT,
            { ...event, ...this.videoTelemetryIds(event.vimeoId) },
        ));

        this.player$.pipe(
            switchMap(player => player.playbackError$),
            takeUntil(this.onDestroy$),
        ).subscribe(event => this.insightsLogger.trackEvent(
            VIDEO_PLAYBACK_ERROR_EVENT,
            { ...event, ...this.videoTelemetryIds(event.vimeoId) },
        ));
    }

    /**
     * Both ids as strings, so they always land in `customDimensions`.
     * Application Insights routes numeric properties to `customMeasurements`
     * instead, which would split `vimeoId` across two bags (it is
     * `number | string`) and make one KQL query unable to see every event.
     */
    private videoTelemetryIds(vimeoId: undefined | number | string) {
        return {
            vimeoId: vimeoId === undefined ? undefined : String(vimeoId),
            slideId: this.video?.slideId === undefined ? undefined : String(this.video.slideId),
        };
    }

    ngAfterViewInit() {
        this.createPlayer()
            .then()
            .catch(async error => {
                this.logger.error(error);
                await this.player.unload();

                // loadVideo error handling:
                // https://github.com/vimeo/player.js/#loadvideooptions-numberstringobject-promisenumberobject-typeerrorpassworderrorerror
                switch (error.name) {
                    case 'TypeError':
                        // the id was not a number
                        break;

                    case 'PasswordError':
                        // the video is password-protected and the viewer needs to enter the
                        // password first
                        break;

                    case 'PrivacyError':
                        // the video is password-protected or private
                        break;

                    default:
                        // some other error occurred
                        break;
                }
            });
    }

    private async createPlayer() {
        const player = new ReactivePlayer(this.vimeoPlayerDomId, this.video, this.globalPlayerConfig);
        this.player = player;
        this.player$.next(player);
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes["video"]) {
            this.logger.log('ngOnChanges video:', changes["video"]);
            this.videoInputSubject.next(changes["video"].currentValue);
        }
        if (changes["muted"]) {
            this.logger.log('ngOnChanges muted:', changes["muted"]);
            this.mutedInputSubject.next(changes["muted"].currentValue);
        }
        if (changes["forceAutoplay"]) {
            this.logger.log('ngOnChanges forceAutoplay:', changes["forceAutoplay"]);
        }
    }

    async ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
        await this.player.destroy();
    }

    private createPlayerOptions(video: VideoPlayerInputProps): VideoPlayerOptions {
        const range = video.range;
        const hasRange = range !== null && range !== undefined  &&  range.end > 0 && range.start >= 0 && range.end > range.start;

        return {
            ...this.defaultPlayerOptions,
            id: video.vimeoId,
            responsive: this.responsive,
            autoplay: this.forceAutoplay || this.autoplay,
            forceAutoplay: this.forceAutoplay,
            muted: this.forceMuted === true || this.muted,
            quality: this.quality as VideoQualityId,
            controls: this.videoControls !== false,
            forceMuted: this.forceMuted === true,
            autopause: true,
            watch_full_video: false,
            start_time: hasRange ? range.start : undefined,
            end_time: hasRange ? range.end : undefined,
            keyboard: this.videoControls, // enable or disable controlling the video using keyboard shortcuts
        };
    }

    getClientWidth = () => this.hostElement.nativeElement.clientWidth;
    getClientHeight = () => this.hostElement.nativeElement.clientHeight;


    seekTo = (seconds: number, paused?: boolean) => this.player.seekTo(seconds, paused);
    restart = () => this.player.restart();
    pauseVideo = () => this.player.pause();
    playVideo = (atPosition?: number) => this.player.play(atPosition);

    activateHotspot = (hotspot: Hotspot) => this.player.activateHotspot(hotspot);
}
